import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";

const Step7FinalQuestions = ({ form, job, setForm, prevStep, nextStep }) => {
  const BASE = process.env.REACT_APP_API_BASE_URL || "";

  const [educations, setEducations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [experienceOptions, setExperienceOptions] = useState([]);
  const isDriver = [2, 8, 11].includes(Number(job?.p_category));
  const selectedRegions = Array.isArray(form.dq_reg_travelled)
    ? form.dq_reg_travelled
    : [];
  const selectedEquipment = Array.isArray(form.dq_equip_operated)
    ? form.dq_equip_operated
    : [];
  const selectedExperience = Array.isArray(form.dq_experience)
    ? form.dq_experience
    : [];
  const equipmentDetails =
    form.dq_equip_operated_detail &&
    typeof form.dq_equip_operated_detail === "object" &&
    !Array.isArray(form.dq_equip_operated_detail)
      ? form.dq_equip_operated_detail
      : {};
  const experienceDetails =
    form.dq_exp_detail &&
    typeof form.dq_exp_detail === "object" &&
    !Array.isArray(form.dq_exp_detail)
      ? form.dq_exp_detail
      : {};

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [eduRes, regRes, equipRes, expRes] = await Promise.all([
          axios.get(`${BASE}/api/dropdowns/highest-edu`),
          axios.get(`${BASE}/api/dropdowns/reg-travelled`),
          axios.get(`${BASE}/api/dropdowns/equip-operated`),
          axios.get(`${BASE}/api/dropdowns/exp_list`),
        ]);

        setEducations(
          eduRes.data.map((e) => ({ value: e.he_id, label: e.he_name }))
        );
        setRegions(
          regRes.data.map((r) => ({ value: r.rt_id, label: r.rt_name }))
        );
        setEquipments(
          equipRes.data.map((eq) => ({ value: eq.eo_id, label: eq.eo_name }))
        );
        setExperienceOptions(
          expRes.data.map((ex) => ({ value: ex.el_id, label: ex.el_name }))
        );
      } catch (err) {
        console.error("âŒ Failed to load dropdowns:", err);
      }
    };
    loadDropdowns();
  }, [BASE]);

  const getLabelById = (list, id) => {
    const item = list.find((el) => String(el.value) === String(id));
    return item?.label || "";
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (e.target.checkValidity()) nextStep();
      }}
    >
      <fieldset className="border p-3 mb-4">
        <legend>Final Qualification Questions</legend>

        {/* Highest Education */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label>
              Highest Level of Education <span className="text-danger">*</span>
            </label>
            <Select
              options={educations}
              value={
                educations.find((opt) => opt.value === form.dq_education) ||
                null
              }
              onChange={(opt) => handleChange("dq_education", opt?.value || "")}
              isClearable
              required
            />
          </div>
          <div className="col-md-6">
            <label>
              School <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="dq_school"
              value={form.dq_school || ""}
              onChange={(e) => handleChange("dq_school", e.target.value)}
              required
            />
          </div>
        </div>

        {job && isDriver && (
          <>
            <h5 className="mt-4 mb-3">Driver Experience</h5>
            {/* Regions Traveled */}
            <div className="mb-3">
              <label>
                Regions Traveled <span className="text-danger">*</span>
              </label>
              <Select
                isMulti
                options={regions}
                value={regions.filter((opt) =>
                  selectedRegions.map(String).includes(String(opt.value))
                )}
                onChange={(val) =>
                  handleChange(
                    "dq_reg_travelled",
                    (val || []).map((opt) => opt.value)
                  )
                }
                required
              />
            </div>

            {/* Equipment Operated */}
            <div className="mb-3">
              <label>
                Equipment Operated <span className="text-danger">*</span>
              </label>
              <Select
                isMulti
                options={equipments}
                value={equipments.filter((opt) =>
                  selectedEquipment.map(String).includes(String(opt.value))
                )}
                onChange={(val) => {
                  const selected = val || [];
                  handleChange(
                    "dq_equip_operated",
                    selected.map((opt) => opt.value)
                  );

                  const updatedDetails = {};
                  selected.forEach((opt) => {
                    updatedDetails[opt.value] = equipmentDetails[opt.value] || {
                      miles: "",
                      desc: "",
                    };
                  });
                  handleChange("dq_equip_operated_detail", updatedDetails);
                }}
                required
              />
            </div>

            {/* Render extra inputs for each equipment */}
            {selectedEquipment.map((id) => {
              const detail = equipmentDetails[id] || {
                miles: "",
                desc: "",
              };
              const label = getLabelById(equipments, id);
              return (
                <div key={id} className="card p-3 mb-2 bg-light">
                  <h6>{label}</h6>
                  <div className="row">
                    <div className="col-md-4">
                      <label># of Miles</label>
                      <input
                        type="number"
                        className="form-control"
                        value={detail.miles}
                        onChange={(e) =>
                          handleChange("dq_equip_operated_detail", {
                            ...equipmentDetails,
                            [id]: { ...detail, miles: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div className="col-md-8">
                      <label>Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={detail.desc}
                        onChange={(e) =>
                          handleChange("dq_equip_operated_detail", {
                            ...equipmentDetails,
                            [id]: { ...detail, desc: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {job && !isDriver && (
          <>
            {/* Experience */}
            <div className="mb-3">
              <label>Experience</label>
              <Select
                isMulti
                className="basic-multi-select"
                classNamePrefix="select"
                value={(experienceOptions || [])
                  .filter((opt) =>
                    selectedExperience.map(String).includes(String(opt.value))
                  )
                  .map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                options={(experienceOptions || []).map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                onChange={(selected) => {
                  const selectedIds = (selected || []).map((s) => s.value);
                  handleChange("dq_experience", selectedIds);

                  // Maintain existing details
                  const newDetail = {};
                  selectedIds.forEach((id) => {
                    newDetail[id] = experienceDetails[id] || {
                      years: "",
                      desc: "",
                    };
                  });

                  handleChange("dq_exp_detail", newDetail);
                }}
              />
            </div>

            {/* Experience Detail Section */}
            {selectedExperience.map((id) => {
              const detail = experienceDetails[id] || {};
              return (
                <div key={id} className="row mb-2 p-3 border rounded bg-light">
                  <strong className="mb-2">
                    {getLabelById(experienceOptions, id)}
                  </strong>

                  <div className="col-md-3">
                    <label>Years Exp.</label>
                    <input
                      type="number"
                      className="form-control"
                      value={detail.years || ""}
                      onChange={(e) =>
                        handleChange("dq_exp_detail", {
                          ...experienceDetails,
                          [id]: {
                            ...(experienceDetails[id] || {}),
                            years: e.target.value,
                          },
                        })
                      }
                    />
                  </div>

                  <div className="col-md-9">
                    <label>Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={detail.desc || ""}
                      onChange={(e) =>
                        handleChange("dq_exp_detail", {
                          ...experienceDetails,
                          [id]: {
                            ...(experienceDetails[id] || {}),
                            desc: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}

        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevStep}
          >
            ← Back
          </button>
          <button type="submit" className="btn btn-primary">
            Next →
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default Step7FinalQuestions;
